"use client";
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { showAlert, setLoading } from '../lib/ui';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true, 'Sending message...');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      showAlert({
        title: 'Message Sent!',
        message: data.message || 'Thank you for your message! We will get back to you soon.',
        type: 'success',
        confirmText: 'OK',
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      showAlert({
        title: 'Validation Incomplete',
        message: error.message || 'Failed to send message. Please try again.',
        type: 'error',
        confirmText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Duffy's Furniture</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get in touch with us for expert furniture & bedding solutions in the Tablelands.
          </p>
        </div>

        {/* Google Map Section */}
        {/* NOTE: Replace the embed URL below with your actual Google Maps embed URL */}
        {/* To get your embed URL: Go to Google Maps > Search your location > Share > Embed a map > Copy the iframe src */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Find Us</h2>
          <div className="bg-gray-100 rounded-lg overflow-hidden shadow-lg">
            <div className="aspect-video w-full">
              <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3809.9709607129785!2d145.4753614!3d-17.2686398!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6982757164a89fc7%3A0x725e8f8cd4334acc!2sDuffy&#39;s%20Furniture%20Court%20%26%20Beds%20R%20Us!5e0!3m2!1sen!2sin!4v1771420870513!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
                title="Duffy's Furniture Location"
              />
            </div>
          </div>
          <div className="mt-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1 text-gray-600">
               <MapPin size={18} className="text-[#0B4866]" />
               <strong>5 Robert St, Atherton QLD 4883</strong>
            </div>
            <p className="text-gray-600">
              200 Byrnes St, Mareeba QLD 4880
            </p>
            <a
              href="https://maps.app.goo.gl/jtHZnWqrn7xZWYuk8"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0B4866] hover:underline mt-2 inline-block"
            >
              Open in Google Maps →
            </a>
          </div>
             <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Get in Touch</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 w-full">
                <div className="flex items-start gap-3">
                  <Phone className="text-[#0B4866] size={20} mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Tele</h3>
                    <p className="text-gray-600">Atherton: (07) 4091 1854</p>
                    <p className="text-gray-600">Mareeba: (07) 4092 3037</p>
                    <p className="text-sm text-gray-500">Mon-Fri 8:30AM-5PM</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Mail className="text-[#0B4866] size={20} mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600 text-sm">admin@duffysfurniture.com.au</p>
                    <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="text-[#0B4866] size={20} mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                    <p className="text-gray-600">5 Robert St, Atherton QLD 4883</p>
                    <p className="text-gray-600">200 Byrnes St, Mareeba QLD 4880</p>
                    <p className="text-sm text-gray-500">Far North Queensland</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="text-[#0B4866] size={20} mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-600">Monday - Friday: 8:30 AM - 5:00 PM</p>
                    <p className="text-gray-600">Saturday: 8:30 AM - 1:00 PM</p>
                    <p className="text-gray-600 font-medium">Sunday: 10:00 AM - 2:00 PM (Atherton Only)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4">Why Choose Duffy's Furniture?</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Family-owned business with local expertise
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Competitive prices
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Wide range of bedding and furniture
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Professional delivery and installation
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
       

          {/* Contact Form */}
          {/* <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Send size={16} className="text-[#0B4866]" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4866] focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Mail size={16} className="text-[#0B4866]" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4866] focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Phone size={16} className="text-[#0B4866]" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4866] focus:border-transparent"
                    placeholder="+61 4XX XXX XXX"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock size={16} className="text-[#0B4866]" />
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4866] focus:border-transparent"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="product">Product Information</option>
                    <option value="delivery">Delivery & Installation</option>
                    <option value="warranty">Warranty & Support</option>
                    <option value="bulk">Bulk Orders</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4866] focus:border-transparent"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#0B4866] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#094058] transition-colors flex items-center justify-center gap-2"
              >
                <Send size={20} />
                Send Message
              </button>
            </form>
          </div> */}
        </div>
      </div>
    </div>
  );
}





